import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ITypeVignetteProductDoneFields } from '@adac-mp/shared/src/contentful-types/TypeVignetteProductDone';
import {
	CART_VALIDATION_ERROR_CODE,
	CartService,
	ICartData,
	ICartOverlapItem,
	IDates,
	IVignetteOffer,
	Locales,
	OfferContext,
	VignetteContext,
	formatDateTimeValues,
	formatPlates,
	formatPlatesPayload,
	formatTripsAmountLabel,
	getLabelValue,
	getUrlBasePath,
	getVignetteDates,
	setRouteLangParam,
} from '@adac-mp/shared';
import {
	Button,
	CartAndSystemValidationModal,
	ContentLoader,
	Modal,
	ProductSummary,
	ProductSummaryElement,
	Setback,
	StartDatePicker,
	Typography,
	renderProductIcon,
} from '../../index';
import { CustomInput } from '../../utils/renderCustomInput';

interface IProductUpsellCardProps {
	offer: IVignetteOffer;
	onProductAddedToCart: () => void;
	onError: (errorMsg: string) => void;
}

export const ProductUpsellCard = ({ offer, onProductAddedToCart, onError }: IProductUpsellCardProps) => {
	const navigate = useNavigate();
	const { lang } = useParams();
	const { labels } = useContext(OfferContext);
	const { cartItems, contentfulLabels, updateCart } = useContext(VignetteContext);
	const [isLoading, setIsLoading] = useState(false);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [productDates, setProductDates] = useState<IDates | undefined>(offer.dates);
	const [hasError, setHasError] = useState(false);
	const [currentOffer, setCurrentOffer] = useState(offer);
	const [validationErrorCode, setValidationErrorCode] = useState<number | undefined>(undefined);
	const [cartOverlapItems, setCartOverlapItems] = useState<ICartOverlapItem[] | []>([]);
	const [cartAndSystemValidationModalIsOpen, setCartAndSystemValidationModalIsOpen] = useState(false);

	const currentLabels = contentfulLabels as ITypeVignetteProductDoneFields;

	const handleCartErrorCode = (res: ICartData) => {
		const errorNumber = res.errors[0]?.errorNumber;
		if (errorNumber === CART_VALIDATION_ERROR_CODE) {
			setValidationErrorCode(errorNumber);
			setCartOverlapItems(res.cartOverlappItems ? res.cartOverlappItems : []);
			setCartAndSystemValidationModalIsOpen(true);
		} else {
			onError(res.errors[0]?.errorText);
		}
	};

	const handleCartUpdateResponse = (res: ICartData) => {
		if (res.code === 200 && res.cart && res.token) {
			updateCart(res.cart);
			onProductAddedToCart();
		} else {
			handleCartErrorCode(res);
		}
	};

	const addProductToCartHandler = (offerToAdd: IVignetteOffer) => {
		const { offerHash } = offerToAdd;
		const { plates, licenseplateType, countryCode } = offer;
		const { checkOutDateTime } = getVignetteDates(productDates);
		const cartHash = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken');
		if (!productDates) {
			setModalIsOpen(true);
			return;
		}
		if (token && cartHash && productDates) {
			setIsLoading(true);
			const requestBody = {
				cartHash,
				checkOutDateTime,
				licenseplate: formatPlatesPayload(plates),
				licenseplateType: licenseplateType ?? 0,
				countryCode,
				offerHash,
				useracceptance: true,
			};
			CartService.insertItemToCart({ body: requestBody, token, lang: lang as Locales }).then((res) => {
				setIsLoading(false);
				handleCartUpdateResponse(res);
			});
		}
	};

	// eslint-disable-next-line complexity
	const renderUpsellProductRows = (vignetteOffer: IVignetteOffer) => {
		const { dates, plates, trips, serviceCatalogName, moduleTypeId } = vignetteOffer;
		const { checkOutDateTime, checkInDateTime } = getVignetteDates(dates || productDates);
		const renderDeleteProductBtn = () => (
			<Typography className="w-full inline-flex justify-end" size="lgEven" variant="title">
				{`+ ${getLabelValue('mp.addProduct', labels)}` || '+ Produkt hinzufügen'}
			</Typography>
		);
		const addProductToCartButton = () => {
			return (
				<ProductSummaryElement
					role="button"
					className="flex-1 order-2"
					value={renderDeleteProductBtn()}
					onClick={() => addProductToCartHandler(currentOffer)}
				/>
			);
		};

		switch (moduleTypeId) {
			case 2:
				return (
					<>
						<ProductSummaryElement
							label={`${getLabelValue('mp.article', labels) || 'Artikel'}`}
							value={serviceCatalogName}
						/>
						<ProductSummaryElement
							className={dates ? 'order-0' : 'order-1'}
							labelColor={dates ? 'title' : 'lightText'}
							label={
								dates
									? `${getLabelValue('mp.valid', labels) || 'Gültig'}`
									: `${currentLabels?.upsellStartDate || 'Bitte legen Sie ein Startdatum fest'}`
							}
							value={
								dates && checkInDateTime
									? `${formatDateTimeValues(
											checkOutDateTime,
											checkInDateTime,
											lang as Locales,
											false,
											labels,
									  )}`
									: null
							}
						/>{' '}
						<ProductSummaryElement
							label={`${getLabelValue('mp.licensePlate', labels) || 'Kennzeichen'}`}
							value={formatPlates(plates)}
						/>
						{addProductToCartButton()}
					</>
				);
			case 3:
				return (
					<>
						<ProductSummaryElement
							label={`${getLabelValue('mp.valid', labels) || 'Gültig'}`}
							value={
								dates && checkInDateTime
									? `${formatDateTimeValues(
											checkOutDateTime,
											checkInDateTime,
											lang as Locales,
											false,
											labels,
									  )}`
									: null
							}
						/>

						<ProductSummaryElement
							label={`${getLabelValue('mp.multipleTrips', labels)}`}
							value={formatTripsAmountLabel(trips, labels)}
						/>
						<ProductSummaryElement
							label={`${getLabelValue('mp.licensePlate', labels) || 'Kennzeichen'}`}
							value={formatPlates(plates)}
						/>
						{addProductToCartButton()}
					</>
				);
			case 4:
				return (
					<>
						<ProductSummaryElement
							label={`${getLabelValue('mp.valid', labels) || 'Gültig'}`}
							value={currentLabels?.cart?.untilFurtherNotice || 'bis auf Widerruf'}
						/>
						<ProductSummaryElement
							label={`${getLabelValue('mp.licensePlate', labels) || 'Kennzeichen'}`}
							value={formatPlates(plates)}
						/>
						{addProductToCartButton()}
					</>
				);
			default:
				return null;
		}
	};

	// eslint-disable-next-line react/display-name
	const renderButtonGroup = () => (className?: string) =>
		(
			<div className={`sticky rounded-b bottom-0 bg-white flex items-center justify-between p-3 ${className}`}>
				<Button
					variant="tertiary"
					size="sm"
					className="group gap-2 bg-white"
					onClick={() => setProductDates(undefined)}>
					<Setback className="group-hover:[&>*]:stroke-gray-100" size="20" />
					{getLabelValue('mp.reset', labels) || 'Zurücksetzen'}
				</Button>
				<Button
					className="px-3 md:px-4"
					onClick={() => {
						if (!productDates) {
							setHasError(true);
							return;
						}
						addProductToCartHandler(currentOffer);
						setProductDates(undefined);
						setHasError(false);
						setModalIsOpen(false);
					}}>
					{getLabelValue('mp.done', labels) || 'Fertig'}
				</Button>
			</div>
		);

	return (
		<>
			<ContentLoader isLoading={isLoading} />
			<ProductSummary
				showProductPrice
				className="flex flex-col"
				category={{ moduleDetail: currentOffer.moduleDetail, moduleDetailId: currentOffer.moduleDetailId }}
				icon={renderProductIcon({
					moduleTypeId: currentOffer.moduleTypeId,
					supplierId: currentOffer.supplierId,
					tollClass: currentOffer.tollClass,
				})}
				title={currentOffer.serviceCatalogName}
				offer={offer}>
				{renderUpsellProductRows(offer)}
			</ProductSummary>
			<Modal
				noHeadline
				fit
				isOpen={modalIsOpen}
				buttonGroup={renderButtonGroup()}
				onClose={() => {
					setModalIsOpen(false);
				}}>
				<StartDatePicker
					inline
					customInput={CustomInput}
					dates={productDates}
					hasError={hasError}
					offer={offer}
					onStartDateChange={(product, newDates) => {
						setCurrentOffer(product as IVignetteOffer);
						setProductDates(newDates);
					}}
				/>
			</Modal>
			{validationErrorCode && (
				<CartAndSystemValidationModal
					errorCode={validationErrorCode}
					cartOverlapItems={cartOverlapItems}
					isOpen={cartAndSystemValidationModalIsOpen}
					onSubmit={() => setCartAndSystemValidationModalIsOpen(false)}
					onCancel={() => {
						if (cartItems.length > 0) {
							setCartAndSystemValidationModalIsOpen(false);
						} else {
							navigate(getUrlBasePath(`/empty-cart${setRouteLangParam(lang)}`), {
								replace: true,
							});
						}
					}}
					onClose={() => setCartAndSystemValidationModalIsOpen(false)}
				/>
			)}
		</>
	);
};
